import React, { useState, useEffect } from 'react';
import { Activity, ShieldCheck, Cpu, HardDrive, Wifi, Clock } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const SystemMonitor = () => {
  const [metrics, setMetrics] = useState({
    cpu: '0.00',
    totalMem: '0.00 GB',
    freeMem: '0.00 GB',
    dbResponseTime: '0 ms',
    uptime: '0.00 hours',
    platform: 'Unknown',
    arch: 'Unknown'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 3000); // refresh every 3 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/system/monitor`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setMetrics(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-center pb-4 border-b border-slate-100">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">System Performance Monitor</h2>
          <p className="text-slate-500 mt-1">Live metrics from Node.js backend server and PostgreSQL database latency checks.</p>
        </div>
        <span className="px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold animate-pulse flex items-center gap-1.5">
          <Wifi size={12} /> Connected
        </span>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-20 text-center shadow-xl">
          <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Pinging server processes...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: CPU */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">CPU Load Average</span>
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg"><Cpu size={20} /></div>
            </div>
            <div className="mt-4">
              <h3 className="text-3xl font-black text-slate-800 tracking-tight">{metrics.cpu}%</h3>
              <div className="w-full bg-slate-100 h-1.5 rounded-full mt-3 overflow-hidden">
                <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, parseFloat(metrics.cpu) * 10)}%` }}></div>
              </div>
            </div>
          </div>

          {/* Card 2: Memory */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Memory Allocated</span>
              <div className="p-2.5 bg-purple-50 text-purple-600 rounded-lg"><HardDrive size={20} /></div>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-black text-slate-800 tracking-tight">{metrics.freeMem} / {metrics.totalMem}</h3>
              <p className="text-xs text-slate-400 mt-2">Available System RAM</p>
            </div>
          </div>

          {/* Card 3: DB Latency */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Database Roundtrip</span>
              <div className="p-2.5 bg-pink-50 text-pink-600 rounded-lg"><Activity size={20} /></div>
            </div>
            <div className="mt-4">
              <h3 className="text-3xl font-black text-slate-800 tracking-tight">{metrics.dbResponseTime}</h3>
              <p className="text-xs text-slate-400 mt-2">PostgreSQL read latency</p>
            </div>
          </div>

          {/* Card 4: Uptime */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Process Uptime</span>
              <div className="p-2.5 bg-yellow-50 text-yellow-600 rounded-lg"><Clock size={20} /></div>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-black text-slate-800 tracking-tight">{metrics.uptime}</h3>
              <p className="text-xs text-slate-400 mt-2">Platform: {metrics.platform} ({metrics.arch})</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemMonitor;
